/**
 * Loop-breaker state for the unprefixed → slug redirect (SD-11900).
 *
 * When `companySlugLoader` can't resolve the URL slug it recovers by bouncing to
 * an unprefixed company-required path. `authLoader` would then re-resolve the
 * `localStorage.companyId` hint to that SAME slug and redirect straight back
 * into the still-failing `/:slug/...` — an infinite client-side redirect chain
 * (both a transient `GET /c/{slug}` blip and a stable 404/403 where the hint
 * keeps resolving to the same rejected slug: stale cache, just-renamed, revoked).
 *
 * The break is deterministic and keyed on the SLUG that actually failed — not on
 * a path + time window, which either false-positives on legitimate same-path
 * navigation or is too short for the loader's retry backoff. `companySlugLoader`
 * marks a slug on a recovery bounce and clears it on a successful resolve;
 * `authLoader` skips the redirect while a slug is marked. It self-heals: the next
 * direct `/:slug/...` navigation (every nav link builds one) resolves and clears
 * the mark, and nothing is ever left in the URL.
 */
const unresolvableSlugs = new Set<string>();

/** Record that `slug` just failed to resolve (companySlugLoader recovery). */
export const markSlugUnresolvable = (slug: string): void => {
  unresolvableSlugs.add(slug);
};

/** Clear the failed mark for `slug` after it resolves successfully. */
export const clearSlugUnresolvable = (slug: string): void => {
  unresolvableSlugs.delete(slug);
};

/** Whether `slug` is currently marked as failing to resolve. */
export const isSlugUnresolvable = (slug: string): boolean =>
  unresolvableSlugs.has(slug);

/**
 * Drop every failure mark. Called on logout so a mark from one session can't
 * survive into the next (logout/re-login navigate within the SPA, keeping this
 * module alive) and block the post-login bare→slug redirect — mirrors how the
 * session lifecycle clears `clearCompanySelectionDedup`. Also used by tests
 * to isolate cases.
 */
export const resetSlugResolutionState = (): void => {
  unresolvableSlugs.clear();
};
